// Super Admin -> Guided Onboarding: master/feature toggles, sessions, analytics.
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Compass, Activity, Clock, Users, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const Toggle = ({ on, onClick, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`w-10 h-5 rounded-full relative transition-colors shrink-0 ${on ? "bg-[#FF5F15]" : "bg-zinc-300"}`}>
    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${on ? "left-5" : "left-0.5"}`} />
  </button>
);

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="border border-zinc-200 bg-white p-4" data-testid={`onboarding-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
      <Icon className="w-3 h-3" /> {label}
    </div>
    <div className="text-2xl font-display font-black">{value}</div>
  </div>
);

const TOGGLE_ROWS = [
  ["onboarding_voice_enabled", "Voice"],
  ["onboarding_text_enabled", "Text chat"],
  ["onboarding_tours_enabled", "Guided tours"],
  ["onboarding_drafts_enabled", "Setup drafts"],
  ["onboarding_escalation_enabled", "Human escalation"],
  ["onboarding_followup_enabled", "Automatic follow-up"],
  ["onboarding_auto_launch_new_orgs", "Auto-launch for new orgs"],
];

export function OnboardingControlPanel() {
  const [settings, setSettings] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/platform/onboarding-settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/platform/onboarding-analytics").then((r) => setAnalytics(r.data)).catch(() => {});
    api.get("/platform/onboarding-sessions").then((r) => setSessions(r.data.items || [])).catch(() => {});
  };
  useEffect(load, []);

  const patch = async (body) => {
    setBusy(true);
    try {
      const { data } = await api.patch("/platform/onboarding-settings", body);
      setSettings((s) => ({ ...s, ...data }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  if (!settings) return <div className="text-xs text-zinc-500 font-mono">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="onboarding-control-panel">
      <div className="border-2 border-zinc-900 bg-white p-5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#09090B] flex items-center justify-center">
            <Compass className="w-5 h-5 text-[#FF5F15]" />
          </div>
          <div>
            <div className="font-display font-black uppercase tracking-tight text-lg">Guided Onboarding — Master switch</div>
            <p className="text-xs text-zinc-500">Turns Fieldlink Guided Onboarding off for every organisation, everywhere.</p>
          </div>
        </div>
        <Toggle on={settings.onboarding_enabled} onClick={() => patch({ onboarding_enabled: !settings.onboarding_enabled })} testId="onboarding-master-toggle" />
      </div>

      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={Activity} label="Sessions today" value={analytics.sessions_today} />
          <StatCard icon={Clock} label="Sessions week" value={analytics.sessions_this_week} />
          <StatCard icon={CheckCircle2} label="Avg completion" value={`${analytics.avg_completion_pct}%`} />
          <StatCard icon={AlertTriangle} label="Escalations" value={analytics.escalations_count} />
        </div>
      )}

      <div className="border border-zinc-200 bg-white">
        <div className="px-4 py-3 border-b border-zinc-200 text-xs font-bold uppercase tracking-wider text-zinc-700">Feature controls</div>
        <div className="divide-y divide-zinc-100">
          {TOGGLE_ROWS.map(([key, label]) => (
            <div key={key} className="flex items-center justify-between gap-3 px-4 py-2.5" data-testid={`onboarding-toggle-row-${key}`}>
              <div className="text-sm font-medium">{label}</div>
              <Toggle on={!!settings[key]} onClick={() => patch({ [key]: !settings[key] })} testId={`onboarding-toggle-${key}`} />
            </div>
          ))}
        </div>
      </div>

      {analytics?.most_skipped_steps?.length > 0 && (
        <div className="border border-zinc-200 bg-white">
          <div className="px-4 py-3 border-b border-zinc-200 text-xs font-bold uppercase tracking-wider text-zinc-700">Most skipped steps</div>
          <div className="divide-y divide-zinc-100">
            {analytics.most_skipped_steps.map((s) => (
              <div key={s.step_id} className="flex items-center justify-between px-4 py-2 text-sm">
                <span>{s.label}</span><span className="font-mono text-zinc-500">{s.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {analytics?.knowledge_gaps?.length > 0 && (
        <div className="border border-zinc-200 bg-white">
          <div className="px-4 py-3 border-b border-zinc-200 text-xs font-bold uppercase tracking-wider text-zinc-700">Knowledge gaps</div>
          <div className="divide-y divide-zinc-100">
            {analytics.knowledge_gaps.map((g, i) => (
              <div key={i} className="flex items-center justify-between px-4 py-2 text-sm gap-3">
                <span className="text-zinc-700">"{g.question}"</span><span className="font-mono text-zinc-500 shrink-0">{g.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="border border-zinc-200 bg-white">
        <div className="px-4 py-3 border-b border-zinc-200 text-xs font-bold uppercase tracking-wider text-zinc-700 flex items-center gap-1.5">
          <Users className="w-3.5 h-3.5" /> Sessions
        </div>
        <div className="divide-y divide-zinc-100 max-h-96 overflow-y-auto">
          {sessions.length === 0 && <div className="px-4 py-6 text-center text-xs text-zinc-400">No onboarding sessions yet.</div>}
          {sessions.map((s) => (
            <div key={s.id} className="flex items-center justify-between px-4 py-2.5 text-sm" data-testid={`onboarding-session-row-${s.id}`}>
              <div>
                <div className="font-medium">{s.org_name} <span className="text-zinc-400">— {s.user_name}</span></div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">{s.status} · {s.current_step_label || "—"}{s.escalated ? " · escalated" : ""}</div>
              </div>
              <span className="font-mono text-zinc-600">{s.progress_pct}%</span>
            </div>
          ))}
        </div>
      </div>

      <Button variant="outline" size="sm" onClick={load} disabled={busy} className="rounded-sm" data-testid="onboarding-refresh-btn">Refresh</Button>
    </div>
  );
}
